<template>
  <div class="dashboard-container">
    <div class="statistics-cards">
      <el-card class="statistic-card animated fadeInUp" data-wow-delay="0.2s">
        <div class="statistic-item">
          <i class="el-icon-document statistic-icon"></i>
          <p class="statistic-label">今日广告记录条数</p>
          <p class="statistic-value">{{ todayAdCount }}</p>
        </div>
      </el-card>
      
      <el-card class="statistic-card animated fadeInUp" data-wow-delay="0.4s">
        <div class="statistic-item">
          <i class="el-icon-money statistic-icon"></i>
          <p class="statistic-label">近一个月总收益</p>
          <p class="statistic-value">¥{{ monthTotalEcpm }}</p>
        </div>
      </el-card>
      
      <el-card class="statistic-card animated fadeInUp" data-wow-delay="0.6s">
        <div class="statistic-item">
          <i class="el-icon-wallet statistic-icon"></i>
          <p class="statistic-label">今日总收益</p>
          <p class="statistic-value">¥{{ todayTotalEcpm }}</p>
        </div>
      </el-card>
      
      <el-card class="statistic-card animated fadeInUp" data-wow-delay="0.8s">
        <div class="statistic-item">
          <i class="el-icon-user statistic-icon"></i>
          <p class="statistic-label">活跃用户数</p>
          <p class="today-users-value">{{ todayActiveUsers }}</p>
          <p class="yesterday-users-value">昨日: {{ yesterdayActiveUsers }}</p>
        </div>
      </el-card>
    </div>
    
    <!-- 收益趋势图表 -->
    <el-card class="chart-card animated fadeIn">
      <div slot="header" class="chart-header">
        <span class="chart-title">{{ chartTitle }}</span>
        <div class="chart-time"><i class="el-icon-timer"></i> {{ currentTimeString }}</div>
        <el-button-group class="chart-toggle">
          <el-button 
            size="mini" 
            :type="currentView === 'hourly' ? 'primary' : ''"
            @click="switchToHourlyView"
            :loading="hourlyDataLoading"
            icon="el-icon-time"
          >
            近24小时
          </el-button>
          <el-button 
            size="mini" 
            :type="currentView === 'daily' ? 'primary' : ''"
            @click="switchToDailyView"
            :loading="dailyDataLoading"
            icon="el-icon-date"
          >
            近一周
          </el-button>
        </el-button-group>
      </div>
      <div id="revenueChart" class="chart-container" ref="revenueChart"></div>
    </el-card>
  </div>
</template>

<script>
import { get } from '@/utils/http';
import * as echarts from 'echarts';
import responsiveMixin from '@/mixins/responsive';

export default {
  name: 'DataDashboard',
  mixins: [responsiveMixin],
  data() {
    return {
      todayAdCount: 0,
      monthTotalEcpm: 0,
      todayTotalEcpm: 0,
      todayActiveUsers: 0,
      yesterdayActiveUsers: 0,
      hourlyRevenueData: [],
      dailyRevenueData: [],
      currentView: 'hourly', // 当前视图: 'hourly' 或 'daily'
      revenueChart: null,
      currentTimeString: '',
      hourlyDataLoading: false, // 标记小时数据是否正在加载
      dailyDataLoading: false // 标记每日数据是否正在加载
    };
  },
  computed: {
    chartTitle() {
      return this.currentView === 'hourly' ? '近24小时收益分布' : '近一周每日收益趋势';
    }
  },
  async mounted() {
    // 初始化动画
    this.initAnimations();
    
    await this.fetchDashboardStatistics();
    await this.fetchHourlyRevenueData();
    
    // 设置当前时间显示
    this.updateCurrentTime();
    setInterval(() => {
      this.updateCurrentTime();
    }, 60000); // 每分钟更新一次
    
    this.$nextTick(() => {
      this.initChart();
      this.updateChart();
    });

    this._resizeChart = () => {
      if (this.revenueChart) {
        this.revenueChart.resize();
      }
    };
    window.addEventListener('resize', this._resizeChart);
  },
  methods: {
    formatRevenue(value) {
      if (value === null || value === undefined || value === '') return '0.00'
      return Number(value).toFixed(2)
    },
    initAnimations() {
      // 动态加载 animate.css
      const head = document.head;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css';
      head.appendChild(link);
    },
    async fetchDashboardStatistics() {
      try {
        const response = await get('/api/ADRevenueDataAdmin/getDashboardStatistics');
        
        if (response.data) {
          this.todayAdCount = response.data['今日广告记录条数'] || 0;
          this.monthTotalEcpm = this.formatRevenue(response.data['近一个月总ecpm'] || 0);
          this.todayTotalEcpm = this.formatRevenue(response.data['今日总ecpm'] || 0);
          this.todayActiveUsers = response.data['今日活跃用户数'] || 0;
          this.yesterdayActiveUsers = response.data['昨日活跃用户数'] || 0;
        }
      } catch (error) {
        console.error('获取数据大屏统计信息失败:', error.message);
        this.$message.error('获取数据失败: ' + error.message);
      }
    },
    async fetchHourlyRevenueData() {
      this.hourlyDataLoading = true;
      
      try {
        const response = await get('/api/ADRevenueDataAdmin/getHourlyRevenueForLast24Hours');
        
        if (response.data) {
          this.hourlyRevenueData = response.data;
          // 如果当前是小时视图，则更新图表
          if (this.currentView === 'hourly') {
            this.$nextTick(() => {
              this.updateChart();
            });
          }
        }
      } catch (error) {
        console.error('获取小时收益数据失败:', error.message);
        this.$message.error('获取小时收益数据失败: ' + error.message);
      } finally {
        this.hourlyDataLoading = false;
      }
    },
    async fetchDailyRevenueData() {
      this.dailyDataLoading = true;
      
      try {
        const response = await get('/api/ADRevenueDataAdmin/getDailyRevenueForLastWeek');
        
        if (response.data) {
          this.dailyRevenueData = response.data;
          
          // 如果当前是天视图，则更新图表
          if (this.currentView === 'daily') {
            this.$nextTick(() => {
              this.updateChart();
            });
          }
        }
      } catch (error) {
        console.error('获取每日收益数据失败:', error.message);
        this.$message.error('获取每日收益数据失败: ' + error.message);
      } finally {
        this.dailyDataLoading = false;
      }
    },
    initChart() {
      this.revenueChart = echarts.init(this.$refs.revenueChart, null, {
        renderer: 'canvas'
      });
      
      // 初始化时设置空的option
      this.revenueChart.setOption({
        title: {
          text: ''
        },
        tooltip: {},
        xAxis: {
          data: []
        },
        yAxis: {},
        series: [{
          type: 'bar'
        }]
      });
    },
    updateChart() {
      if (!this.revenueChart) {
        this.revenueChart = echarts.init(this.$refs.revenueChart);
      }
      
      let option;
      
      if (this.currentView === 'hourly') {
        // 小时视图 - 柱状图
        const hours = this.hourlyRevenueData.map(item => `${item.hour}时`);
        const values = this.hourlyRevenueData.map(item => Number(item.total_ecpm) || 0);
        
        option = {
          tooltip: {
            trigger: 'axis',
            formatter: '{b}: ¥{c}',
            backgroundColor: 'rgba(0,0,0,0.8)',
            borderColor: '#5470c6',
            borderWidth: 1,
            textStyle: {
              color: '#fff'
            }
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '15%',
            top: '8%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: hours,
            axisLabel: {
              interval: 0,
              rotate: 45,
              fontSize: 12
            },
            axisLine: {
              lineStyle: {
                color: '#dcdfe6'
              }
            }
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: '¥{value}',
              fontSize: 12
            },
            axisLine: {
              lineStyle: {
                color: '#dcdfe6'
              }
            },
            splitLine: {
              lineStyle: {
                type: 'dashed',
                color: '#e4e7ed'
              }
            }
          },
          series: [{
            name: '收益',
            data: values,
            type: 'bar',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#83bff6' },
                { offset: 0.5, color: '#188df0' },
                { offset: 1, color: '#188df0' }
              ])
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#2378f7' },
                  { offset: 0.7, color: '#2378f7' },
                  { offset: 1, color: '#83bff6' }
                ])
              }
            }
          }]
        };
      } else {
        // 天视图 - 折线图
        const dates = this.dailyRevenueData.map(item => item.date);
        const values = this.dailyRevenueData.map(item => Number(item.total_ecpm) || 0);
        
        option = {
          tooltip: {
            trigger: 'axis',
            formatter: '{b}: ¥{c}',
            backgroundColor: 'rgba(0,0,0,0.8)',
            borderColor: '#91cc75',
            borderWidth: 1,
            textStyle: {
              color: '#fff'
            }
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '15%',
            top: '8%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: dates,
            axisLabel: {
              interval: 0,
              rotate: 45,
              fontSize: 12
            },
            axisLine: {
              lineStyle: {
                color: '#dcdfe6'
              }
            }
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: '¥{value}',
              fontSize: 12
            },
            axisLine: {
              lineStyle: {
                color: '#dcdfe6'
              }
            },
            splitLine: {
              lineStyle: {
                type: 'dashed',
                color: '#e4e7ed'
              }
            }
          },
          series: [{
            name: '收益',
            data: values,
            type: 'line',
            smooth: true,
            symbolSize: 8,
            itemStyle: {
              color: '#91cc75'
            },
            lineStyle: {
              color: '#91cc75',
              width: 3
            },
            areaStyle: {
              opacity: 0.3,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(145, 204, 117, 0.5)' },
                { offset: 1, color: 'rgba(145, 204, 117, 0.1)' }
              ])
            },
            emphasis: {
              itemStyle: {
                color: '#7ecf62',
                borderColor: '#fff',
                borderWidth: 2
              }
            }
          }]
        };
      }
      
      this.revenueChart.setOption(option);
    },
    async switchToHourlyView() {
      if (this.currentView !== 'hourly') {
        this.currentView = 'hourly';
        // 每次切换到小时视图时重新获取数据
        await this.fetchHourlyRevenueData();
        this.$nextTick(() => {
          this.updateChart();
        });
      } else {
        // 如果已经在小时视图，也重新获取数据
        await this.fetchHourlyRevenueData();
      }
      // 切换视图时更新时间显示
      this.updateCurrentTime();
    },
    async switchToDailyView() {
      if (this.currentView !== 'daily') {
        this.currentView = 'daily';
        // 每次切换到每日视图时重新获取数据
        await this.fetchDailyRevenueData();
        this.$nextTick(() => {
          this.updateChart();
        });
      } else {
        // 如果已经在每日视图，也重新获取数据
        await this.fetchDailyRevenueData();
      }
      // 切换视图时更新时间显示
      this.updateCurrentTime();
    },
    updateCurrentTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hour = String(now.getHours()).padStart(2, '0');
      const minute = String(now.getMinutes()).padStart(2, '0');
      
      this.currentTimeString = `${year}-${month}-${day} ${hour}:${minute}`;
    }
  },
  beforeDestroy() {
    if (this._resizeChart) {
      window.removeEventListener('resize', this._resizeChart);
    }
    if (this.revenueChart) {
      this.revenueChart.dispose();
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  padding: 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%);
  min-height: auto;
  font-family: 'Nunito', sans-serif;
}

.statistics-cards {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 30px;
}

.statistic-card {
  flex: 1;
  min-width: 250px;
  margin-bottom: 0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(107, 142, 235, 0.15);
  border: none;
  transition: all 0.3s ease;
}

.statistic-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(107, 142, 235, 0.25);
}

.statistic-item {
  text-align: center;
  padding: 20px;
}

.statistic-icon {
  font-size: 36px;
  margin-bottom: 15px;
  display: block;
  width: 70px;
  height: 70px;
  line-height: 70px;
  text-align: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%);
  color: white;
  margin: 0 auto 15px;
}

.statistic-label {
  font-size: 15px;
  color: #718096;
  margin-bottom: 12px;
  font-weight: 600;
}

.statistic-value {
  font-size: 32px;
  font-weight: 800;
  color: #2d3748;
  margin: 10px 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  background: linear-gradient(90deg, #4f46e5, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.today-users-value {
  font-size: 32px;
  font-weight: 800;
  color: #2d3748;
  margin: 10px 0 5px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  background: linear-gradient(90deg, #10b981, #059669);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.yesterday-users-value {
  font-size: 15px;
  color: #909399;
  margin: 5px 0 0 0;
  font-weight: 600;
}

.chart-card {
  margin-top: 20px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(107, 142, 235, 0.15);
  border: none;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid #f4f4f5;
}

.chart-title {
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
}

.chart-time {
  font-size: 14px;
  color: #909399;
  margin-right: 20px;
}

.chart-toggle {
  margin-left: auto;
}

.chart-container {
  height: 450px;
  width: 100%;
}

@media (max-width: 768px) {
  .statistics-cards {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }

  .statistic-card {
    min-width: 100%;
    flex: none;
  }

  .statistic-item {
    padding: 14px;
  }

  .statistic-icon {
    width: 56px;
    height: 56px;
    line-height: 56px;
    font-size: 28px;
  }

  .statistic-value,
  .today-users-value {
    font-size: 24px;
  }

  .chart-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 12px;
  }

  .chart-title {
    font-size: 16px;
  }

  .chart-time {
    margin-right: 0;
    font-size: 12px;
  }

  .chart-toggle {
    margin-left: 0;
    display: flex;
    width: 100%;
  }

  .chart-toggle .el-button {
    flex: 1;
  }

  .chart-container {
    height: 280px;
  }
}
</style>